import React from "react";
import { ColDef } from "ag-grid-community";
import { useAtom } from "jotai";
import { useSetAtom } from "jotai/index";

import { Table } from "components/Table/Table";
import { RowRender } from "components/RowRender/RowRender";

import {
  TDocItemToDraw,
  TDocument,
  TDocumentMainColumn,
  TDocumentToDraw,
} from "../../types/documents";
import {
  documentsToDrawAtom,
  openedDocumentAtom,
  selectedDocumentAtom,
} from "../../atoms/documentsAtom";
import { setDocumentModalAtom } from "../../atoms/modalAtom/modalAtom";
import useDocuments from "../../hooks/useDocuments";
import useWindowSizes from "../../hooks/useWindowSizes";

export interface ColsData {
  isConducted: boolean | -1;
  number: number;
  action: string;
  creationTime: string;
  stock: string;
  provider: string;
  client: string;
  comment: string;
  sum: number;
  receivingStock: string;
  amountSales: number;
  available: boolean;
}

type BodyProps = {
  mainColumns?: TDocumentMainColumn[];
  bottomRow?: ({
    documentsToDraw,
  }: {
    documentsToDraw: TDocumentToDraw[];
  }) => TDocumentToDraw;
};

export const Body = ({ mainColumns, bottomRow }: BodyProps): JSX.Element => {
  const { openDocument } = useDocuments();
  const { height } = useWindowSizes();

  const [documentsToDraw] = useAtom(documentsToDrawAtom);
  const [selectedDocument, setSelectedDocument] = useAtom(selectedDocumentAtom);

  const colDefs = mainColumns?.map((i) => ({
    ...i,
    cellRenderer: RowRender,
    resizable: false,
  }));

  const onRowClicked = (event) => {
    if (event.node.rowPinned) return;

    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    if (selectedData[0]?.docid) {
      if (selectedData[0].docid === selectedDocument?.docid) {
        openDocument();
      } else {
        setSelectedDocument(selectedData[0]);
      }
    }
  };

  if (!mainColumns) return <></>;

  return (
    <Table
      className="pb-5"
      height={Math.max(height - 300, 500)}
      rowData={documentsToDraw}
      colDefs={colDefs as any}
      rowSelection={"single"}
      onRowClicked={onRowClicked}
      rowStyle={{ cursor: "pointer" }}
      getRowStyle={(params) => ({
        backgroundColor:
          params.data.docid === selectedDocument?.docid
            ? "var(--secondaryColor)"
            : "unset",
        fontWeight:
          params.data.docid === selectedDocument?.docid ? "500" : "unset",
        color:
          params.data.docid === selectedDocument?.docid
            ? "var(--primaryColor)"
            : "unset",
      })}
      pinnedBottomRowData={
        bottomRow && documentsToDraw?.length
          ? [bottomRow({ documentsToDraw })]
          : undefined
      }
    />
  );
};
